import { Component, OnInit, Input, SimpleChanges, Output, EventEmitter, forwardRef } from '@angular/core';
import { 
    IonGrid, IonRow, IonCol, IonIcon
} from '@ionic/angular/standalone';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import moment from 'moment';

import { MarvDateToStringPipe } from '@marv/pipes';
import { MarvDateService, MarvWeekDay } from '@marv/services';
import { MarvTabsComponent, MarvTabComponent, MarvTab, MarvButtonComponent, MarvCircleComponent } from '@marv/components';

class DayPreview {
    date: Date;
    dateStr: string;
    nbEvents: number;
    disabled: boolean;
}

class MonthPreview {
    month: Date;
    days: Array<DayPreview> = [];
    constructor(month: Date){
        this.month = month;
    }
}

class Week extends Array<{day: DayPreview, today: boolean, otherMonth: boolean, publicHoliday: boolean}> {}

class StatsWeekDays{
    day: Date; 
    nbEvents: number;
}

class DatesPreviewStats {
    global: number = 0;
    globalMonth: number = 0;
    weekDays: Array<StatsWeekDays> = [];
}

export class DatesPreview {
    
    dates: Array<Date> = [];    
    addedDates: Array<Date> = [];
    deletedDates: Array<Date> = [];
    finalDates: Array<Date> = [];    
    
    constructor(dates: Array<Date> = [], addedDates: Array<Date> = [], deletedDates: Array<Date> = []){
        this.dates = dates;
        this.addedDates = addedDates;
        this.deletedDates = deletedDates;
        this.finalDates = this.getFinalDates();
    }
    
    getFinalDates(){
        let dates: Array<Date> = [];
        let datesMap: Map<string, boolean> = new Map<string, boolean>;
        
        this.dates.forEach(d => {
            let dStr = moment(d).format('YYYY-MM-DD');
            datesMap.set(dStr, true);
        });
        
        this.addedDates.forEach(d => {
            let dStr = moment(d).format('YYYY-MM-DD');
            if(!datesMap.has(dStr)){
                datesMap.set(dStr, true);
            }
        });
        
        this.deletedDates.forEach(d => {
            let dStr = moment(d).format('YYYY-MM-DD');
            if(datesMap.has(dStr)){
                datesMap.delete(dStr);
            }     
        });
        
        datesMap.forEach((v, k) => {
            dates.push(moment(k).toDate());
        });
        
        dates.sort((a, b) => {                
            return a.getTime() - b.getTime();                
        });
        
        return dates;
    }
}

@Component({
    selector: 'app-dates-preview',
    templateUrl: './dates-preview.component.html',
    styleUrls: ['./dates-preview.component.scss'],
    imports: [
        IonGrid, IonRow, IonCol, IonIcon, TranslatePipe, MarvDateToStringPipe, 
        forwardRef(() => MarvTabsComponent), forwardRef(() => MarvTabComponent),
        forwardRef(() => MarvButtonComponent), forwardRef(() => MarvCircleComponent)
    ]
})
export class DatesPreviewComponent  implements OnInit {

    @Input({alias : 'datesPreview', required : true}) datesPreview: DatesPreview;
    @Input({alias : 'dateMin', required : false}) dateMin: Date;
    @Input({alias : 'dateMax', required : false}) dateMax: Date;
    @Input({alias : 'editable', required : false}) editable: boolean = false;
    @Input({alias : 'highlightePublicHolidays', required : false}) highlightePublicHolidays: boolean = true;
    @Output() onChangeDates = new EventEmitter<DatesPreview|null>();
    
    tabs: Array<MarvTab> = [
        {id: 'calendar', label: this.translateService.instant('CALENDAR'), iconPrefix: 'calendar-outline', defaultSelected: true},
        {id: 'list', label: this.translateService.instant('LIST'), iconPrefix: 'list-outline'},      
    ];

    addedDates: Map<string, boolean> = new Map<string, boolean>;
    deletedDates: Map<string, boolean> = new Map<string, boolean>;
    currentMonth: Date = new Date();
    weekDays: Array<MarvWeekDay> = MarvDateService.GetWeekDays(true); 
    weeks: Array<Week> = [];
    months: Array<MonthPreview> = [];
    stats: DatesPreviewStats = new DatesPreviewStats();    
    
    constructor(
        private translateService: TranslateService
    ) { }

    ngOnInit() {}
    
    ngOnChanges(changes: SimpleChanges) {        
        if(changes['datesPreview']){   
            this.addedDates = new Map<string, boolean>;
            this.deletedDates = new Map<string, boolean>;
            if(this.datesPreview?.addedDates?.length > 0){
                this.datesPreview.addedDates.forEach(d => {
                     this.addedDates.set(moment(d).format('YYYY-MM-DD'), true);
                });
            }
            if(this.datesPreview?.deletedDates?.length > 0){
                this.datesPreview.deletedDates.forEach(d => {
                     this.deletedDates.set(moment(d).format('YYYY-MM-DD'), true);
                });
            }
            this.initDays();
        }
    }
    
    initDays(init: boolean = true){        
        let months: Array<MonthPreview> = [];
        let currentMonth = new Date();  
        
        let dates = this.datesPreview.dates.slice();
        this.addedDates.forEach((d, k) => {
            dates.push(moment(k).toDate());
        });
        
        if(dates.length > 0){      
        
            dates.sort((a, b) => {                
                return a.getTime() - b.getTime();                
            });
              
            currentMonth = MarvDateService.GetFirstDayOfMonth(dates[0]);
            let monthsMap = new Map<string, MonthPreview>;
            dates.forEach(d => {
                let month = MarvDateService.GetFirstDayOfMonth(d);
                let monthStr = moment(month).format('YYYY-MM-DD')
                let existMonth = monthsMap.get(monthStr);
                if(!existMonth){
                    existMonth = new MonthPreview(month);
                }
               
                let indexDay = existMonth.days.findIndex(day => day.dateStr ===  moment(d).format('YYYY-MM-DD'));
                if(indexDay === -1){
                    existMonth.days.push({
                        date: d,
                        dateStr: moment(d).format('YYYY-MM-DD'),
                        nbEvents: 1,
                        disabled: false
                    });
                }
                else{
                    existMonth.days[indexDay].nbEvents++;
                }                
                
                monthsMap.set(monthStr, existMonth);
            });
            monthsMap.forEach(m => {
                months.push(m);
            });
        }
        if(init){
            this.currentMonth = currentMonth;
        }
        this.months = months;
        this.stats.global = (dates.length || 0) - this.deletedDates.size;
        this.displayDays();
    }
    
    disablePreviousMonth(): boolean{
        return this.dateMin && moment(this.dateMin).format("YYYY-MM") === moment(this.currentMonth).format("YYYY-MM");
    }
    
    disableNextMonth(): boolean{
        return this.dateMax && moment(this.dateMax).format("YYYY-MM") === moment(this.currentMonth).format("YYYY-MM");
    }

    previousMonth(){
        if(!this.disablePreviousMonth()){
            this.currentMonth = MarvDateService.GetPreviousMonth(this.currentMonth);
            this.displayDays();
        }
    }

    nextMonth(){
        if(!this.disableNextMonth()){
            this.currentMonth = MarvDateService.GetNextMonth(this.currentMonth);
            this.displayDays();
        }
    }
    
    displayDays(){
        
        let weeks: Array<Week> = [];
        let week: Week = new Week();
        let nbEventsMonth = 0;
        let statsWeekDays: Array<StatsWeekDays> = [];
        let startMonth = MarvDateService.GetFirstDayOfMonth(this.currentMonth);        
        let endMonth = MarvDateService.GetLastDayOfMonth(this.currentMonth);
        let start = moment(MarvDateService.GetFirstDayOfWeek(startMonth));        
        let end = moment(MarvDateService.GetLastDayOfWeek(endMonth));
        let todayStr = moment().format('YYYY-MM-DD');
        
        while(start <= end){
            
            let dayOfWeek = start.isoWeekday();
            
            let nbEvents = 0;
            let nbEventsAdded = 0;
            let nbEventsDeleted = 0;
            let otherMonth = false;
            if(moment(startMonth).format('YYYY-MM-DD') <= start.format('YYYY-MM-DD') && start.format('YYYY-MM-DD') <= moment(endMonth).format('YYYY-MM-DD')){
                nbEvents = this.datesPreview.dates.filter(d => 
                    moment(d).format('YYYY-MM-DD') === start.format('YYYY-MM-DD')
                ).length;            
                nbEventsAdded = (this.addedDates.has(start.format('YYYY-MM-DD')) ? 1 : 0);
                nbEventsDeleted = (this.deletedDates.has(start.format('YYYY-MM-DD')) ? 1 : 0);
            }
            else{
                otherMonth = true;
            }
            
            week[dayOfWeek] = {
                day:  {
                    date: start.toDate(), 
                    dateStr: start.format('YYYY-MM-DD'),
                    nbEvents: nbEvents,
                    disabled: ( (this.dateMin && start.format("YYYY-MM-DD") < moment(this.dateMin).format("YYYY-MM-DD")) || (this.dateMax && start.format("YYYY-MM-DD") > moment(this.dateMax).format("YYYY-MM-DD")) )
                },
                today: (todayStr === start.format('YYYY-MM-DD')),
                otherMonth: otherMonth,
                publicHoliday: MarvDateService.IsPublicHolidays(start.toDate())
            };               
            nbEventsMonth += (nbEvents + nbEventsAdded - nbEventsDeleted);
            
            if(typeof statsWeekDays[dayOfWeek] === 'undefined'){
                statsWeekDays[dayOfWeek] = {
                    day: start.toDate(), 
                    nbEvents: 0
                };
            }
            statsWeekDays[dayOfWeek].nbEvents += (nbEvents + nbEventsAdded - nbEventsDeleted);
            
            if(dayOfWeek === 7){
                weeks.push(week);
                week = [];
            }
            
            start.add(1, 'day');
        }
        
        this.weeks = weeks;
        this.stats.globalMonth = nbEventsMonth;
        this.stats.weekDays = statsWeekDays.filter(s => s.nbEvents > 0);
    }
    
    deleteDate(date: DayPreview){
        if(!date.disabled){
            if(this.addedDates.has(date.dateStr)){
                this.addedDates.delete(date.dateStr);
            }
            else{
                this.deletedDates.set(date.dateStr, true);
            }
            this.initDays(false);
        }
    }
    
    addDate(date: DayPreview){
        if(!date.disabled){
            if(this.deletedDates.has(date.dateStr)){
                this.deletedDates.delete(date.dateStr);
            }
            else{
                this.addedDates.set(date.dateStr, true);
            }
            this.initDays(false);
        }
    }
    
    validateDates(){        
        let addedDates: Array<Date> = [];
        this.addedDates.forEach((v, k) => {
            addedDates.push(moment(k).toDate());
        });
       
        let deletedDates: Array<Date> = [];
        this.deletedDates.forEach((v, k) => {
            deletedDates.push(moment(k).toDate());
        });
       
        let datesPreviewChange = new DatesPreview(this.datesPreview.dates, addedDates, deletedDates);        
        this.onChangeDates.emit(datesPreviewChange);
    }
    
    cancel(){
        this.onChangeDates.emit(null);
    }
}
